import { useCallback } from 'react';
import { usePolling } from '../../hooks/usePolling';
import { endpoints, type PlannerPlan } from '../../services/api';
import { startMicrosoft, useSession } from '../../store/session';
import { addDays, dateKey } from '../../lib/calendarRange';
import { useSettings, type GanttPeriod, type PlannerView } from '../../store/settings';
import { ghostButtonClass, Choice, inputClass, Section, Toggle, WidgetPage } from './controls';

const VIEWS: Array<{ id: PlannerView; name: string; description: string }> = [
  { id: 'kanban', name: 'Board', description: 'Kanban columns, one per bucket' },
  { id: 'list', name: 'List', description: 'Every task in a list, soonest first' },
  { id: 'gantt', name: 'Gantt chart', description: 'Tasks as bars on a timeline of weeks' },
  { id: 'calendar', name: 'Calendar', description: 'This month, each task on its due date' },
];

const PERIODS: Array<{ id: GanttPeriod; name: string; description: string }> = [
  { id: 'auto', name: 'Auto', description: 'From last week to the last open task (4–13 weeks)' },
  { id: 'all', name: 'All', description: 'From the earliest task date to the latest' },
  { id: 'period', name: 'Set period', description: 'Your own From and To dates' },
  { id: 'rolling', name: 'Rolling', description: 'Days, weeks or months either side of today' },
];

const UNIT_NAME = { days: 'Days', weeks: 'Weeks', months: 'Months' } as const;
/** Up to about three years either side (the chart is capped at that length anyway). */
const ROLLING_MAX = { days: 730, weeks: 104, months: 24 } as const;

/** Whole numbers from 0 up to the unit's maximum. */
function clampRolling(before: number, after: number, unit: keyof typeof ROLLING_MAX) {
  const clamp = (n: number) => Math.min(ROLLING_MAX[unit], Math.max(0, Math.floor(Number.isFinite(n) ? n : 0)));
  return { ganttBefore: clamp(before), ganttAfter: clamp(after) };
}

/** Which Planner plans the widget shows (in the order they were ticked). */
function PlanChooser() {
  const chosen = useSettings((s) => s.planner.plans);
  const update = useSettings((s) => s.update);
  const { data, loading, error } = usePolling('planner-plans-settings', useCallback(() => endpoints.plannerPlans().then((r) => r.plans), []), 10 * 60_000);
  if (!data) return <p className="muted text-xs">{loading ? 'Loading your Planner plans…' : error}</p>;
  if (!data.length) {
    return <p className="muted text-sm">No Planner plans found. Planner comes with Microsoft 365 work and school accounts.</p>;
  }
  const isOn = (p: PlannerPlan) => chosen.some((c) => c.id === p.id && (c.account ?? '') === (p.account ?? ''));
  const toggle = (p: PlannerPlan) =>
    update('planner', {
      plans: isOn(p)
        ? chosen.filter((c) => !(c.id === p.id && (c.account ?? '') === (p.account ?? '')))
        : [...chosen, { id: p.id, name: p.name, account: p.account, accountEmail: p.accountEmail }],
    });
  // Several Microsoft accounts: a heading for each account's plans.
  const several = new Set(data.map((p) => p.account)).size > 1;
  return (
    <ul className="space-y-1.5 pl-1">
      {data.map((p, i) => (
        <li key={`${p.account ?? ''}:${p.id}`}>
          {several && p.account !== data[i - 1]?.account && (
            <div className="muted pb-1 pt-2 text-xs font-semibold uppercase tracking-wider">{p.accountEmail ?? 'Microsoft account'}</div>
          )}
          <label className="flex cursor-pointer items-center gap-3 text-sm">
            <input type="checkbox" checked={isOn(p)} onChange={() => toggle(p)} className="accent-[var(--accent)]" />
            {p.name}
          </label>
        </li>
      ))}
    </ul>
  );
}

/** Settings → Widgets → Planner. */
export function PlannerTab() {
  const { config, microsoft } = useSession();
  const planner = useSettings((s) => s.planner);
  const update = useSettings((s) => s.update);
  // Work accounts that can't yet read colleagues' names (assignees then show as a plain icon).
  // Set period starts from last week to four weeks ahead, so the dates aren't blank.
  const choosePeriod = (ganttPeriod: GanttPeriod) => {
    const today = new Date();
    const seed = ganttPeriod === 'period' && (!planner.ganttFrom || !planner.ganttTo);
    update('planner', seed ? { ganttPeriod, ganttFrom: dateKey(addDays(today, -7)), ganttTo: dateKey(addDays(today, 28)) } : { ganttPeriod });
  };
  const needNames = (microsoft?.accounts ?? []).filter((a) => a.connected && a.work && !a.people);

  return (
    <WidgetPage
      label="Show Planner"
      description="Your Microsoft Planner boards as a Kanban board, list, Gantt chart or calendar. Make changes in Planner; the screen updates within a couple of minutes."
      enabled={planner.enabled}
      onChange={(enabled) => update('planner', { enabled })}
    >
      <Section title="Plans" description={planner.plans.length > 1 ? 'With several plans, tabs switch between them.' : undefined}>
        {!config?.features.microsoft ? (
          <p className="muted text-sm">Microsoft sign-in isn’t available on this server.</p>
        ) : microsoft?.connected ? (
          <PlanChooser />
        ) : (
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="muted">Connect your work or school Microsoft account first.</span>
            <button className="shrink-0 text-[var(--accent)] hover:underline" onClick={() => startMicrosoft('link')}>
              Connect Microsoft
            </button>
          </div>
        )}
        {planner.plans.length > 1 && (
          <>
            <p className="pt-2 text-sm">Move to the next plan</p>
            <Choice
              options={[
                { id: '0', name: 'Never' },
                { id: '30', name: 'Every 30 s' },
                { id: '60', name: 'Every minute' },
                { id: '300', name: 'Every 5 min' },
              ]}
              value={String(planner.rotateSec)}
              onChange={(v) => update('planner', { rotateSec: Number(v) })}
              columns={4}
            />
          </>
        )}
      </Section>

      <Section title="View">
        <Choice options={VIEWS} value={planner.view} onChange={(view) => update('planner', { view })} />
        {planner.view === 'list' && (
          <>
            <p className="pt-2 text-sm">Group tasks</p>
            <Choice
              options={[
                { id: 'bucket', name: 'By bucket' },
                { id: 'due', name: 'By due date' },
                { id: 'none', name: 'Don’t group' },
              ]}
              value={planner.groupBy}
              onChange={(groupBy) => update('planner', { groupBy })}
              columns={3}
            />
          </>
        )}
        {planner.view === 'gantt' && (
          <>
            <p className="pt-2 text-sm">Period</p>
            <Choice options={PERIODS} value={planner.ganttPeriod} onChange={choosePeriod} />
            {planner.ganttPeriod === 'period' && (
              <div className="grid grid-cols-2 gap-2">
                <label className="text-sm">
                  From
                  <input type="date" className={`${inputClass} mt-1`} value={planner.ganttFrom} onChange={(e) => update('planner', { ganttFrom: e.target.value })} />
                </label>
                <label className="text-sm">
                  To
                  <input
                    type="date"
                    className={`${inputClass} mt-1`}
                    value={planner.ganttTo}
                    min={planner.ganttFrom || undefined}
                    onChange={(e) => update('planner', { ganttTo: e.target.value })}
                  />
                </label>
                {planner.ganttFrom && planner.ganttTo && planner.ganttTo < planner.ganttFrom && (
                  <p className="col-span-2 text-xs text-amber-400">To is before From, so the chart uses Auto until that’s fixed.</p>
                )}
              </div>
            )}
            {planner.ganttPeriod === 'rolling' && (
              <>
                <Choice
                  options={[
                    { id: 'days', name: 'Days' },
                    { id: 'weeks', name: 'Weeks' },
                    { id: 'months', name: 'Months' },
                  ]}
                  value={planner.ganttUnit}
                  onChange={(ganttUnit) => update('planner', { ganttUnit, ...clampRolling(planner.ganttBefore, planner.ganttAfter, ganttUnit) })}
                  columns={3}
                />
                <div className="grid grid-cols-2 gap-2">
                  <label className="text-sm">
                    {UNIT_NAME[planner.ganttUnit]} before today
                    <input
                      type="number"
                      min={0}
                      max={ROLLING_MAX[planner.ganttUnit]}
                      className={`${inputClass} mt-1`}
                      value={planner.ganttBefore}
                      onChange={(e) => update('planner', clampRolling(Number(e.target.value), planner.ganttAfter, planner.ganttUnit))}
                    />
                  </label>
                  <label className="text-sm">
                    {UNIT_NAME[planner.ganttUnit]} after today
                    <input
                      type="number"
                      min={0}
                      max={ROLLING_MAX[planner.ganttUnit]}
                      className={`${inputClass} mt-1`}
                      value={planner.ganttAfter}
                      onChange={(e) => update('planner', clampRolling(planner.ganttBefore, Number(e.target.value), planner.ganttUnit))}
                    />
                  </label>
                </div>
              </>
            )}
            <Toggle label="Group by bucket" checked={planner.ganttGroup} onChange={(ganttGroup) => update('planner', { ganttGroup })} />
            <p className="muted text-xs">Tasks run from the earliest start date at the top. Grouped, each bucket has its own waterfall.</p>
          </>
        )}
        {(planner.view === 'gantt' || planner.view === 'calendar') && (
          <p className="muted text-xs">Weeks start on the day chosen under Widgets → Calendar.</p>
        )}
      </Section>

      <Section title="Show">
        <Toggle label="Completed tasks (last 30 days)" checked={planner.showCompleted} onChange={(showCompleted) => update('planner', { showCompleted })} />
        <Toggle label="Labels" checked={planner.showLabels} onChange={(showLabels) => update('planner', { showLabels })} />
        <Toggle label="Who tasks are assigned to" checked={planner.showAssignees} onChange={(showAssignees) => update('planner', { showAssignees })} />
        {planner.showAssignees && needNames.length > 0 && (
          <div className="space-y-2 rounded-lg border border-[var(--border)] p-3 text-sm">
            <p className="muted">
              To show colleagues’ initials, allow KCL Canvas to read their names. This also allows SharePoint and shared items. Until then, other
              people show as a plain icon.
            </p>
            <div className="flex flex-wrap gap-2">
              {needNames.map((a) => (
                <button key={a.id} className={ghostButtonClass} onClick={() => startMicrosoft('link', undefined, true, a.id)}>
                  Allow names{needNames.length > 1 && a.email ? ` for ${a.email}` : ''}
                </button>
              ))}
            </div>
          </div>
        )}
      </Section>

      <Section title="Text size">
        <Choice
          options={[
            { id: '0.85', name: 'S' },
            { id: '1', name: 'M' },
            { id: '1.2', name: 'L' },
            { id: '1.4', name: 'XL' },
          ]}
          value={String(planner.textSize)}
          onChange={(v) => update('planner', { textSize: Number(v) })}
          columns={4}
        />
      </Section>
    </WidgetPage>
  );
}
